/**
 * MembershipPage：学习会员订阅页（阶段三）
 * - 顶部展示当前等级/到期（来自 useMembershipStore.me）
 * - 套餐卡片（服务端仅下发 enabled）：选择套餐 + 支付渠道 → 下单出码
 * - 支付卡：打开支付链接（h5/native），前端每 3s 轮询查单；服务端核实已支付后自动发放
 * - 支付完成刷新 me（等级即时生效，学习页读时复核随之放行）
 * 未登录 → 引导到「我的」页登录。价格/权益事实源在服务端，本页只展示与下单。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChartStore } from '../state/store';
import { useMembershipStore } from '../state/membership-store';
import {
  confirmMemberOrder,
  createMemberOrder,
  fetchMemberPackages,
  getMemberOrder,
  PAY_CHANNEL_ALIPAY,
  type MemberOrder,
  type MemberPackage,
} from '@shared/api/membership';

function levelLabel(level: string): string {
  if (level === 'pro') return '高级会员';
  if (level === 'basic') return '基础会员';
  return '免费';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function MembershipPage() {
  const token = useChartStore((s) => s.token);
  const me = useMembershipStore((s) => s.me);

  const [packages, setPackages] = useState<MemberPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // 微信 App 支付尚未接入（H5 渠道被 armor 禁用），本期默认并仅开放支付宝
  const [channel, setChannel] = useState<number>(PAY_CHANNEL_ALIPAY);
  // 下单流程状态：idle → ordering（下单中）→ paying（待支付轮询）→ paid（成功）
  const [phase, setPhase] = useState<'idle' | 'ordering' | 'paying' | 'paid'>('idle');
  const [order, setOrder] = useState<MemberOrder | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchMemberPackages(token)
      .then((items) => {
        if (!cancelled) setPackages(items);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // 卸载时清理轮询
  useEffect(() => stopPolling, [stopPolling]);

  const onPaid = useCallback(() => {
    stopPolling();
    setPhase('paid');
    void useMembershipStore.getState().refresh(token);
  }, [stopPolling, token]);

  const startPolling = useCallback(
    (orderNo: string) => {
      stopPolling();
      pollRef.current = window.setInterval(async () => {
        try {
          const st = await getMemberOrder(token, orderNo);
          if (st.status === 2) onPaid();
          else if (st.status === 3) {
            stopPolling();
            setErr('订单已超时，请重新下单');
            setPhase('idle');
            setOrder(null);
          }
        } catch {
          // 单次轮询失败忽略，下次再来
        }
      }, 3000);
    },
    [stopPolling, token, onPaid],
  );

  const handleSubscribe = async (pkg: MemberPackage) => {
    if (!token) return;
    setErr('');
    setPhase('ordering');
    try {
      const o = await createMemberOrder(token, {
        membership_package_id: pkg.id,
        payment_method: channel,
        client_type: 'h5',
      });
      setOrder(o);
      if (o.status === 2) {
        onPaid();
        return;
      }
      setPhase('paying');
      startPolling(o.order_no);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase('idle');
    }
  };

  // 用户完成支付回到页面时，主动 confirm 一次（不依赖轮询间隔）
  const handlePaidClick = async () => {
    if (!order) return;
    setErr('');
    try {
      const o = await confirmMemberOrder(token, order.order_no);
      if (o.status === 2) onPaid();
      else setErr('尚未检测到支付完成，请稍候…');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const cancelOrder = () => {
    stopPolling();
    setPhase('idle');
    setOrder(null);
    setErr('');
  };

  const payLink = order?.h5_url || order?.payment_url || '';
  const sortedPackages = useMemo(() => [...packages].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id), [packages]);

  return (
    <div className="flex flex-col gap-4 pb-4">
      {/* 当前会员状态 */}
      <div className="rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-4 text-white shadow">
        <div className="text-xs opacity-80">当前订阅</div>
        <div className="mt-1 text-lg font-semibold">
          {token ? levelLabel(me?.level || 'free') : '未登录'}
        </div>
        {token && me?.expire_at && (
          <div className="mt-0.5 text-xs opacity-80">到期：{fmtDate(me.expire_at)}</div>
        )}
        {token && me?.expire_at == null && me?.level && me.level !== 'free' && (
          <div className="mt-0.5 text-xs opacity-80">永久有效</div>
        )}
      </div>

      {!token && (
        <div className="rounded-lg bg-white px-4 py-6 text-center text-sm text-slate-500 shadow">
          登录后即可订阅会员 ·
          <a href="#/mine" className="ml-1 font-medium text-indigo-600 hover:underline">
            去登录
          </a>
        </div>
      )}

      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</div>}

      {/* 支付进行中的订单卡 */}
      {phase === 'paying' && order && (
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-slate-800">扫码 / 跳转支付</div>
            <button type="button" onClick={cancelOrder} className="text-xs text-slate-400 hover:text-slate-600">
              取消
            </button>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            订单号 {order.order_no} · 金额 ¥{Number(order.amount).toFixed(2)}
          </div>
          {payLink && (
            <a
              href={payLink}
              target="_blank"
              rel="noreferrer"
              className="mt-3 block w-full rounded-md bg-indigo-600 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-indigo-700"
            >
              打开支付页完成付款
            </a>
          )}
          <div className="mt-2 flex items-center justify-center gap-2 text-xs text-slate-400">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500" />
            正在等待支付结果…
          </div>
          <button
            type="button"
            onClick={() => void handlePaidClick()}
            className="mt-2 w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            我已完成支付
          </button>
        </div>
      )}

      {phase === 'paid' && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          支付成功，会员权益已生效 🎉
          <button type="button" onClick={cancelOrder} className="ml-2 underline">
            继续
          </button>
        </div>
      )}

      {/* 支付渠道选择 */}
      {token && (phase === 'idle' || phase === 'ordering') && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">支付方式：</span>
          <button
            type="button"
            disabled
            title="暂未开放"
            className="cursor-not-allowed rounded-full bg-slate-100 px-3 py-1 text-slate-400"
          >
            微信支付（暂未开放）
          </button>
          <button
            type="button"
            onClick={() => setChannel(PAY_CHANNEL_ALIPAY)}
            className={`rounded-full px-3 py-1 ${channel === PAY_CHANNEL_ALIPAY ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 shadow'}`}
          >
            支付宝
          </button>
        </div>
      )}

      {/* 套餐列表 */}
      <div className="flex flex-col gap-3">
        {loading && <div className="py-10 text-center text-sm text-slate-400">加载中…</div>}
        {!loading && sortedPackages.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-400">暂无可购买套餐</div>
        )}
        {sortedPackages.map((pkg) => {
          const busy = phase === 'ordering' || phase === 'paying' || phase === 'paid';
          return (
            <div key={pkg.id} className="flex items-center justify-between rounded-lg bg-white px-4 py-3.5 shadow">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-800">{pkg.name}</span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-px text-[11px] ${
                      pkg.level === 'pro' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'
                    }`}
                  >
                    {levelLabel(pkg.level)}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-slate-400">
                  {pkg.duration_days === 0 ? '永久有效' : `${pkg.duration_days} 天`}
                </div>
              </div>
              <div className="ml-3 shrink-0 text-right">
                <div className="text-base font-semibold text-slate-900">¥{Number(pkg.price).toFixed(2)}</div>
                {pkg.original_price != null && (
                  <div className="text-xs text-slate-400 line-through">¥{Number(pkg.original_price).toFixed(2)}</div>
                )}
                <button
                  type="button"
                  disabled={!token || busy}
                  onClick={() => void handleSubscribe(pkg)}
                  className="mt-1 rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  {phase === 'ordering' ? '下单中…' : '订阅'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
